import { useMemo } from 'react';
import type { BusPosition, Stop } from '../lib/types';
import { haversineDistance, estimateETA, stopsAway, formatDistance } from '../utils/geo';

interface ETADisplayProps {
  position: BusPosition | null;
  stops: Stop[];
  studentStopId: string | null;
}

export function ETADisplay({ position, stops, studentStopId }: ETADisplayProps) {
  const info = useMemo(() => {
    if (!position || !stops.length || !studentStopId) return null;

    const targetIdx = stops.findIndex((s) => s.id === studentStopId);
    if (targetIdx === -1) return null;

    const targetStop = stops[targetIdx];
    const distance = haversineDistance(
      position.lat,
      position.lng,
      targetStop.latitude,
      targetStop.longitude
    );
    const eta = estimateETA(distance, position.speed);
    const remaining = stopsAway(
      position.lat,
      position.lng,
      targetIdx,
      stops
    );

    return { distance, eta, remaining, stopName: targetStop.name };
  }, [position, stops, studentStopId]);

  if (!info) {
    return (
      <div className="eta-display eta-waiting">
        Waiting for bus location...
      </div>
    );
  }

  return (
    <div className="eta-display">
      <div className="eta-stop-name">{info.stopName}</div>
      <div className="eta-details">
        <div className="eta-item">
          <span className="eta-value">
            {info.remaining === 0 && info.distance < 500 ? 'Arrived!' : `${info.remaining} stops`}
          </span>
          <span className="eta-label">away</span>
        </div>
        <div className="eta-item">
          <span className="eta-value">{formatDistance(info.distance)}</span>
          <span className="eta-label">distance</span>
        </div>
        <div className="eta-item">
          <span className="eta-value">
            {info.eta !== null ? `${info.eta} min` : '--'}
          </span>
          <span className="eta-label">ETA</span>
        </div>
      </div>
    </div>
  );
}
